import React, { useState } from 'react'
import { MenuItem, Dropdown } from 'react-bootstrap'

import CardContainer from 'live/components/ui/CardContainer'
import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'

import { EntityErrorModal, FilteredSearch, IListItemError, Scrollbars } from '../../../../../generic'
import { IHazard } from '../../../../../types'
import { IDiagramTopEvent, IDiagramConsequence, IDiagramThreat, IDiagramTypes } from '../../RiskDiagramEditor'
import { ConsequenceThreatContainer } from './styles'

type IProps = {
    hazard: IHazard
    topEvent: IDiagramTopEvent
    threats: IDiagramThreat[]
    consequences: IDiagramConsequence[]
    onListClick: (type: ISelectedType, item?: IDiagramThreat | IDiagramConsequence) => void
    onListDeleteClick: (type: IDiagramTypes, newList: (IDiagramThreat | IDiagramConsequence)[]) => void
    errors: IListItemError[]
}
type ISelectedType = 'HAZARD' | 'TOPEVENT' | 'THREAT' | 'CONSEQUENCE'

type IDefaultDirection = 'ASC' | 'DESC'

type ListItem = {
    type: ISelectedType
    name: string
    id?: string | number
    uid?: string | number
    operation?: { description: string; id: string | number }
    frequency?: string
    reference?: IDiagramThreat | IDiagramConsequence
}

export const ConsequenceThreatList: React.FC<IProps> = ({
    consequences,
    hazard,
    onListClick,
    onListDeleteClick,
    threats,
    topEvent,
    errors
}) => {
    const [selectedType, setSelectedType] = useState<ISelectedType | 'ALL'>('ALL')
    const [searchedString, setSearchedString] = useState<string>('')
    const [direction, setDirection] = useState<IDefaultDirection>(null)
    const [errorsModal, setErrorsModal] = useState(false)
    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN

    const onDeleteClick = (type: IDiagramTypes, item: IDiagramThreat | IDiagramConsequence): void => {
        let newArray: (IDiagramThreat | IDiagramConsequence)[]
        if (type === 'THREAT') {
            newArray = threats.filter(threat => threat !== item)
            threats = [...newArray] as IDiagramThreat[]
        } else if (type === 'CONSEQUENCE') {
            newArray = consequences.filter(consequence => consequence !== item)
            consequences = [...newArray] as IDiagramConsequence[]
        }
        onListDeleteClick(type, newArray)
    }

    const handleSort = (a: ListItem, b: ListItem): number => {
        if (direction) {
            if (a.name < b.name) {
                return direction === 'ASC' ? -1 : 1
            }

            if (a.name > b.name) {
                return direction === 'ASC' ? 1 : -1
            }
        }

        return 0
    }

    const mountList = (
        hazard: IHazard,
        topEvent: IDiagramTopEvent,
        threats: IDiagramThreat[],
        consequences: IDiagramConsequence[]
    ): ListItem[] => {
        const items: ListItem[] = []
        if (hazard) {
            items.push({
                type: 'HAZARD',
                id: hazard.id,
                name: hazard.name,
                operation: hazard.operation
            })
        }

        if (topEvent) {
            items.push({
                type: 'TOPEVENT',
                id: topEvent.id,
                name: topEvent.name
            })
        }
        if (threats) {
            threats.map(threat => {
                items.push({
                    type: 'THREAT',
                    uid: threat.uid,
                    name: threat.name,
                    frequency: threat.initialFrequency,
                    reference: threat
                })
            })
        }
        if (consequences) {
            consequences.map(consequence => {
                items.push({
                    type: 'CONSEQUENCE',
                    id: consequence.id,
                    uid: consequence.uid,
                    name: consequence.name,
                    frequency: consequence.thresholdFrequency,
                    reference: consequence
                })
            })
        }

        return items
    }

    return (
        <>
            <ConsequenceThreatContainer>
                <div className="header">
                    <div className="header-title">{i18n('LIST OF ENTITIES')}</div>
                    <div className="header-subtitle">
                        {2 + threats.length + consequences.length} {i18n('items')}
                    </div>
                    <button className="header-error-alert" onClick={() => errors.length && setErrorsModal(true)}>
                        <i className={`far fa-bell ${errors.length > 0 ? 'has-errors' : ''}`}></i>
                    </button>
                </div>
                <div className="row-div">
                    <FilteredSearch onSearch={setSearchedString} keyword={searchedString} />
                    <Dropdown pullRight id="sort-button">
                        <Dropdown.Toggle>
                            <i className="far fa-sort-alt" />
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <MenuItem active={!direction} onClick={() => setDirection(null)}>
                                {i18n('Default')}
                            </MenuItem>
                            <MenuItem active={direction === 'ASC'} onClick={() => setDirection('ASC')}>
                                {i18n('Ascending')}
                            </MenuItem>
                            <MenuItem active={direction === 'DESC'} onClick={() => setDirection('DESC')}>
                                {i18n('Descending')}
                            </MenuItem>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <div className="type-bar">
                    <div className={selectedType === 'ALL' ? 'active' : ''} onClick={() => setSelectedType('ALL')}>
                        {i18n('All')}
                    </div>
                    <div
                        className={selectedType === 'HAZARD' ? 'active' : ''}
                        onClick={() => setSelectedType('HAZARD')}
                    >
                        {i18n('Hazard')}
                    </div>
                    <div
                        className={selectedType === 'TOPEVENT' ? 'active' : ''}
                        onClick={() => setSelectedType('TOPEVENT')}
                    >
                        {i18n('Top Event')}
                    </div>
                    <div
                        className={selectedType === 'THREAT' ? 'active' : ''}
                        onClick={() => setSelectedType('THREAT')}
                    >
                        {i18n('Threats')}
                    </div>
                    <div
                        className={selectedType === 'CONSEQUENCE' ? 'active' : ''}
                        onClick={() => setSelectedType('CONSEQUENCE')}
                    >
                        {i18n('Consequences')}
                    </div>
                </div>
                <div className="consequence-threat-list">
                    <Scrollbars>
                        {mountList(hazard, topEvent, threats, consequences)
                            .sort(handleSort)
                            .filter(item => (selectedType === 'ALL' ? true : item.type.includes(selectedType)))
                            .filter(
                                item =>
                                    !item.name?.length ||
                                    item.name?.toLowerCase().includes(searchedString.toString().toLowerCase())
                            )
                            .map((item, index) => (
                                <CardContainer className={`custom-card ${item.type.toLowerCase()}`} key={index}>
                                    <div className="main-row" onClick={() => onListClick(item.type, item.reference)}>
                                        <div className={`main-col custom-card-border ${item.type.toLowerCase()}`} />
                                        <div className="main-col info">
                                            <div className="row-card">
                                                <div className="title">{i18n(item.type)}</div>
                                                <div className="id">{item.id ? `#${item.id}` : ''}</div>
                                            </div>
                                            <div className="card-description">{item.name ? item.name : '-'}</div>
                                        </div>
                                    </div>
                                    {bowtieAdmin && (
                                        <div
                                            className="delete-button"
                                            onClick={() => onDeleteClick(item.type, item.reference)}
                                        >
                                            {item.type !== 'HAZARD' && item.type !== 'TOPEVENT' && (
                                                <i className="fal fa-trash-alt" />
                                            )}
                                        </div>
                                    )}
                                </CardContainer>
                            ))}
                    </Scrollbars>
                </div>
            </ConsequenceThreatContainer>
            <EntityErrorModal onClose={() => setErrorsModal(false)} show={errorsModal} errorsList={errors} />
        </>
    )
}
